import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from 'primitives/context-menu'
import React from 'react'
import { type Shortcut, ShortcutKey } from 'shared/components/shortcut-key'

export type MenuItem<T> = {
  type: 'item' | 'checkbox' | 'radio' | 'separator' | 'sub' | 'label'
  label?: string
  icon?: React.ReactNode
  checked?: boolean
  value?: string
  items?: MenuItem<T>[]
  disabled?: boolean
  visible?: boolean
  shortcut?: Shortcut
  onSelect?: (context?: T | HTMLElement) => void
  // Triggers the onSelect keyboard shortcut only if there is an hovered element
  onlyOnHover?: boolean
}

type ContextMenuProps<T> = {
  children?: React.ReactNode
  className?: string
  menuItems?: MenuItem<T>[]
}

interface MenuItemWithShortcutProps {
  label?: string
  shortcut?: Shortcut
}
const MenuItemWithShortcut: React.FC<MenuItemWithShortcutProps> = ({ label, shortcut }) => (
  <>
    {label}
    {shortcut && (
      <ContextMenuShortcut>
        <ShortcutKey shortcut={shortcut} />
      </ContextMenuShortcut>
    )}
  </>
)

/**
 * Generic context menu component
 * @param items - Array of menu items
 * @param children - Children to wrap the context menu around
 *
 * **Important**: The context menu will only open if the target is part of an element with data-hover attribute
 */
export const GenericContextMenu = <T,>({ children, className, menuItems }: ContextMenuProps<T>) => (
  <ContextMenu
    data-testid="context-menu-root"
    onOpenChange={(open) => {
      if (!open) {
        document.querySelector('.context-menu-open')?.classList.remove('context-menu-open')
      }
    }}
  >
    <ContextMenuTrigger
      data-testid="context-menu"
      className={className}
      onContextMenu={(e) => {
        // ** IMPORTANT **: Note to future dev
        // This is the condition you are wondering why the context menu is not showing up
        // Only open the context menu if the target is part of element with data-hover attribute
        const target = (e.target as HTMLElement)?.closest('[data-hover]')
        if (!target) {
          e.preventDefault()
          return
        }
        target.classList.add('context-menu-open')
        // Get context menu and pass the event data to the method
      }}
    >
      {children}
    </ContextMenuTrigger>
    {!!menuItems?.length && (
      <ContextMenuContent className="min-w-64">
        {menuItems.map((item) => {
          if (item.visible === false) return null
          switch (item.type) {
            case 'item':
              return (
                <ContextMenuItem
                  key={item.label}
                  inset
                  disabled={item.disabled}
                  onClick={(e) => e.stopPropagation()}
                  onSelect={() => item.onSelect?.()}
                >
                  {item.icon && <div className="mr-2 -ml-4">{item.icon}</div>}
                  <MenuItemWithShortcut label={item.label} shortcut={item.shortcut} />
                </ContextMenuItem>
              )
            case 'label':
              return (
                <React.Fragment key={item.label}>
                  <ContextMenuLabel inset>{item.label}</ContextMenuLabel>
                  <ContextMenuSeparator />
                </React.Fragment>
              )
            case 'checkbox':
              return (
                <ContextMenuCheckboxItem
                  key={item.label}
                  checked={item.checked}
                  onSelect={() => item.onSelect?.()}
                >
                  <MenuItemWithShortcut label={item.label} shortcut={item.shortcut} />
                </ContextMenuCheckboxItem>
              )
            case 'radio':
              return (
                <ContextMenuRadioItem
                  key={item.label}
                  value={item.value || ''}
                  onSelect={() => item.onSelect?.()}
                >
                  {item.label}
                </ContextMenuRadioItem>
              )
            case 'separator':
              return <ContextMenuSeparator key={item.label} />
            case 'sub':
              return (
                <ContextMenuSub key={item.label}>
                  <ContextMenuSubTrigger inset>{item.label}</ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-48">
                    {item.items?.map((subItem) => (
                      <ContextMenuItem key={subItem.label} onSelect={() => subItem.onSelect?.()}>
                        <MenuItemWithShortcut label={subItem.label} shortcut={subItem.shortcut} />
                      </ContextMenuItem>
                    ))}
                  </ContextMenuSubContent>
                </ContextMenuSub>
              )
            default:
              return null
          }
        })}
      </ContextMenuContent>
    )}
  </ContextMenu>
)
